/**
 * UnifiedDataGrid - Centralized DataGrid component for all entity types
 *
 * A wrapper around GlobalDataGrid that provides:
 * - Config-driven behavior based on EntityConfig
 * - Integrated inline and mass update via useUnifiedGridUpdate
 * - Automatic cell editability and highlighting
 * - Default selection footer
 *
 * Usage:
 * ```tsx
 * <UnifiedDataGrid
 *   config={TEST_ENTITY_CONFIG}
 *   rows={rows}
 *   columns={columns}
 *   apiRef={apiRef}
 *   options={{ users, contacts, organizations }}
 *   mockMode={true}
 *   onMockUpdate={applyUpdates}
 * />
 * ```
 */

import { Box, SxProps, Theme } from '@mui/material'
import {
  GridApiPremium,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridSortModel,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid-premium'
import { useSearchData } from 'api/search'
import React, { useCallback, useMemo, useState } from 'react'
import GlobalDataGrid from '../GlobalDataGrid'
import { UnifiedSelectionFooter } from './components/UnifiedSelectionFooter'
import { getEntityConfig } from './config/entityConfigs'
import { useUnifiedGridUpdate } from './hooks/useUnifiedGridUpdate'
import { EntityConfig, EntityType, GridOptions } from './types/EntityConfig'

interface BatchUpdatePayload {
  tableName: string
  PK: string
  SK: string
  fieldName: string
  newValue: any
}

export interface UnifiedDataGridProps<TRow = any> {
  /** Entity type (uses predefined config) */
  entityType?: EntityType
  /** Custom entity configuration (overrides entityType) */
  config?: EntityConfig<TRow>

  /** Data rows */
  rows: TRow[]
  /** Column definitions */
  columns: GridColDef[]
  /** Grid API ref */
  apiRef: React.RefObject<GridApiPremium>

  /** Options data for edit cells (users, contacts, organizations, etc.) */
  options?: GridOptions

  /** Server-side mode props */
  serverSideProps?: {
    rowCount: number
    paginationModel: { page: number; pageSize: number }
    onPaginationModelChange: (model: { page: number; pageSize: number }) => void
    onSortModelChange: (model: GridSortModel) => void
    onFilterModelChange: (model: any) => void
    aggregations?: Record<string, number>
    syncStatus?: { documentCount?: number; lastSyncTime?: string; isInitialized?: boolean }
  }

  /** Callbacks */
  onRowClick?: (row: TRow) => void
  onDeleteClick?: (selectedIds: Set<string | number>) => void
  onMassUpdateComplete?: (count: number, field: string, value: any) => void

  /** Mock mode - don't call real API */
  mockMode?: boolean
  /** Callback for mock mode to update local state */
  onMockUpdate?: (updates: BatchUpdatePayload[], normalizedFields: Record<string, any>) => void

  /** Snackbar function for notifications */
  showSnackbar?: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void

  /** Custom components */
  toolbarComponent?: React.ComponentType<any>
  FooterComponent?: React.ComponentType<any>
  footerProps?: any

  /** Style overrides */
  gridSxProps?: SxProps<Theme>
  gridContainerSx?: SxProps<Theme>

  /** Feature flags */
  isLoading?: boolean
  checkboxSelection?: boolean
}

export const UnifiedDataGrid = <TRow extends Record<string, any>>({
  entityType,
  config: customConfig,
  rows,
  columns,
  apiRef,
  options,
  serverSideProps,
  onRowClick,
  onDeleteClick,
  onMassUpdateComplete,
  mockMode = false,
  onMockUpdate,
  showSnackbar,
  toolbarComponent,
  FooterComponent,
  footerProps,
  gridSxProps,
  gridContainerSx,
  isLoading,
  checkboxSelection = true,
}: UnifiedDataGridProps<TRow>) => {
  // Resolve configuration
  const config = useMemo(() => {
    if (customConfig) return customConfig
    if (entityType) return getEntityConfig(entityType)
    throw new Error('UnifiedDataGrid requires either entityType or config prop')
  }, [customConfig, entityType])

  // Selection state
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    ids: new Set(),
    type: 'include',
  })
  const { organizations, isLoading: isSearchLoading, isFetching } = useSearchData()
  // Unified update hook
  const { processRowUpdate, isUpdating } = useUnifiedGridUpdate({
    config,
    rowSelectionModel,
    apiRef,
    options,
    rows,
    onMassUpdateComplete,
    mockMode,
    onMockUpdate,
    showSnackbar,
    showNotifications: !!showSnackbar,
  })

  // Cell editability based on config
  const isCellEditable = useCallback(
    (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) => {
      return config.editableFields.includes(params.field)
    },
    [config.editableFields]
  )

  // Cell class names for mass update highlighting
  const getCellClassName = useCallback(
    (params: GridCellParams) => {
      const isMultiSelect = rowSelectionModel?.ids?.size > 1
      const isEditableField = config.editableFields.includes(params.field)
      const isSelected = rowSelectionModel?.ids?.has(params.id)

      if (isMultiSelect && isEditableField && isSelected) {
        return 'mass-update-target'
      }
      return ''
    },
    [rowSelectionModel, config.editableFields]
  )

  // Resolve footer component
  const ResolvedFooter = FooterComponent || UnifiedSelectionFooter
  const resolvedFooterProps = footerProps || {
    config,
    rowSelectionModel,
    setRowSelectionModel,
    apiRef,
    onDeleteClick,
    itemLabel: config.labels.plural,
    serverSideMode: config.dataMode === 'server',
    totalRowCount: rows.length,
  }

  // Wrapper for processRowUpdate to match GlobalDataGrid's expected signature
  const handleProcessRowUpdate = useCallback(
    async (newRow: any, oldRow: any) => {
      return processRowUpdate(newRow, oldRow)
    },
    [processRowUpdate]
  )
  const orgRows = useMemo(() => {
    if (!organizations || !organizations?.length || isSearchLoading || isFetching) return []
    return organizations?.map(o => ({ ...o, rowId: o?.PK }))
  }, [organizations])
  const orgColumns = [
    {
      field: 'name',
      headerName: 'Name',
      valueGetter: (_, row) => row?.name,
    },
    {
      field: 'status',
      headerName: 'Status',
      valueGetter: (_, row) => row?.status,
      editable: true,
    },
  ]
  return (
    <Box sx={{ height: '100%', width: '100%', ...gridContainerSx }}>
      <GlobalDataGrid
        rows={orgRows}
        columns={orgColumns}
        apiRef={apiRef}
        context={config.context}
        // Defaults from config
        defaultFilterModel={config.defaults.filterModel}
        defaultVisibilityModel={config.defaults.visibilityModel}
        defaultSortModel={config.defaults.sortModel}
        defaultGridDensity={config.defaults.density}
        pinnedColumns={config.defaults.pinnedColumns}
        defaultColumnOrder={config.defaults.columnOrder}
        // Tree data
        treeData={config.treeData}
        // Selection
        checkboxSelection={checkboxSelection}
        onRowSelectionModelChange={setRowSelectionModel}
        // Editing
        isCellEditable={isCellEditable}
        getCellClassName={getCellClassName}
        onProcessRowUpdate={handleProcessRowUpdate}
        allowInlineMassEdit={true}
        // Row click
        onRowClick={onRowClick ? row => onRowClick((row as unknown) as TRow) : undefined}
        // Components
        toolbarComponent={toolbarComponent}
        FooterComponent={ResolvedFooter as React.FC<any>}
        footerProps={resolvedFooterProps}
        // Server-side mode
        serverSideMode={config.dataMode === 'server'}
        {...(config.dataMode === 'server' &&
          serverSideProps && {
            rowCount: serverSideProps.rowCount,
            paginationModel: serverSideProps.paginationModel,
            onPaginationModelChange: serverSideProps.onPaginationModelChange,
            onSortModelChange: serverSideProps.onSortModelChange,
            onFilterModelChange: serverSideProps.onFilterModelChange,
            serverAggregations: serverSideProps.aggregations,
            syncStatus: serverSideProps.syncStatus,
          })}
        // Style
        gridSxProps={{
          // Highlight editable cells when multiple rows selected
          '& .mass-update-target': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.12)',
            },
          },
          ...gridSxProps,
        }}
        isLoading={isLoading || isUpdating}
      />
    </Box>
  )
}

export default UnifiedDataGrid
